import React, { createContext, useContext, useState, useEffect } from 'react';

// Define the CartItem type
export interface CartItem {
  id: string;
  asin: string;
  name: string;
  image: string;
  quantity: number;
}

// Define the CartContextType
interface CartContextType {
  cartItems: CartItem[];
  removedItem: CartItem | null;
  addToCart: (item: CartItem) => void;
  addMany: (items: CartItem[]) => void;
  removeFromCart: (id: string) => void;
  undoRemove: () => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  getCartTotal: () => number;
  isCartOpen: boolean;
  toggleCart: (isOpen?: boolean) => void;
}

// Create the context with a default value
const CartContext = createContext<CartContextType>({
  cartItems: [],
  removedItem: null,
  addToCart: () => {},
  addMany: () => {},
  removeFromCart: () => {},
  undoRemove: () => {},
  updateQuantity: () => {},
  clearCart: () => {},
  getCartTotal: () => 0,
  isCartOpen: false,
  toggleCart: () => {},
});

// Custom hook to use the cart context
export const useCart = () => useContext(CartContext);

// CartProvider component
export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [removedItem, setRemovedItem] = useState<CartItem | null>(null);
  const [removeTimeout, setRemoveTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Load cart from localStorage on initial render
  useEffect(() => {
    const storedCart = localStorage.getItem('cart');
    if (storedCart) {
      try {
        setCartItems(JSON.parse(storedCart));
      } catch (error) {
        console.error('Failed to parse cart from localStorage:', error);
        localStorage.removeItem('cart');
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  // Clear the removal timeout when component unmounts
  useEffect(() => {
    return () => {
      if (removeTimeout) {
        clearTimeout(removeTimeout);
      }
    };
  }, [removeTimeout]);

  // Toggle cart drawer
  const toggleCart = (isOpen?: boolean) => {
    setIsCartOpen(isOpen !== undefined ? isOpen : !isCartOpen);
  };

  // Add item to cart
  const addToCart = (item: CartItem) => {
    setCartItems(prevItems => {
      // Check if item already exists in cart
      const existingItemIndex = prevItems.findIndex(cartItem => cartItem.id === item.id);
      
      if (existingItemIndex >= 0) {
        // Update quantity if item exists
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + item.quantity
        };
        return updatedItems;
      } else {
        // Add new item if it doesn't exist
        return [...prevItems, item];
      }
    });
  };

  // Add several items at once (bulk add from table view)
  const addMany = (items: CartItem[]) => {
    items.forEach((item) => addToCart(item));
  };

  // Remove item from cart
  const removeFromCart = (id: string) => {
    // Find the item to be removed
    const itemToRemove = cartItems.find(item => item.id === id);
    
    if (itemToRemove) {
      // Store the removed item for potential undo
      setRemovedItem(itemToRemove);
      
      // Remove the item from the cart
      setCartItems(prevItems => prevItems.filter(item => item.id !== id));
      
      // Clear any existing timeout
      if (removeTimeout) {
        clearTimeout(removeTimeout);
      }
      
      // Set a new timeout to clear the removed item after 5 seconds
      const timeout = setTimeout(() => {
        setRemovedItem(null);
      }, 5000);
      
      setRemoveTimeout(timeout);
    }
  };
  
  // Undo the last item removal
  const undoRemove = () => {
    if (removedItem) {
      // Add the removed item back to the cart
      setCartItems(prevItems => [...prevItems, removedItem]);
      
      // Clear the removed item
      setRemovedItem(null);
      
      // Clear the timeout
      if (removeTimeout) {
        clearTimeout(removeTimeout);
        setRemoveTimeout(null);
      }
    }
  };

  // Update item quantity
  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(id);
      return;
    }
    
    setCartItems(prevItems => 
      prevItems.map(item => 
        item.id === id ? { ...item, quantity } : item
      )
    );
  };

  // Clear cart
  const clearCart = () => {
    setCartItems([]);
  };

  // Get cart total (number of items)
  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      removedItem,
      addToCart,
      addMany,
      removeFromCart,
      undoRemove,
      updateQuantity,
      clearCart,
      getCartTotal,
      isCartOpen,
      toggleCart
    }}>
      {children}
    </CartContext.Provider>
  );
};